import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PackageOpen, Printer } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDate, fmtWeekday } from "../../../lib/format";
import { errMsg } from "../orders/QueueOrder";
import { useApi } from "../../customer/hooks";
import { StatusBadge } from "../../../components/common/StatusBadge";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";

const OrderCard = ({ o }) => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const step = o.status === "prepared" ? "loaded" : ["submitted", "received"].includes(o.status) ? "prepared" : null;
  const mark = async () => {
    setBusy(true);
    try { await api.post(`/staff/orders/${o.local_id}/progress`, { step }); qc.invalidateQueries({ queryKey: ["warehouse"] }); toast.success(t("admin.saved")); } catch (e) { toast.error(errMsg(e, t)); } finally { setBusy(false); }
  };
  return (
    <li className="flex flex-wrap items-center gap-3 py-3" data-testid={`wh-order-${o.local_id}`}>
      <Link to={`/staff/orders/${o.local_id}`} className="min-w-0 flex-1"><p className="truncate font-semibold text-navy">{o.customer_name}</p><p className="text-xs text-slate-500">{o.doc_no} · {o.items.length} {t("orders.lines").toLowerCase()}</p></Link>
      <StatusBadge status={o.status} />
      {step && <button type="button" disabled={busy} onClick={mark} data-testid={`wh-mark-${o.local_id}`} className="h-11 rounded-full bg-brand px-4 text-sm font-semibold text-white disabled:opacity-60">{t(step === "loaded" ? "ops.markLoaded" : "ops.markPrepared")}</button>}
    </li>
  );
};

export default function Warehouse() {
  const { t, lang } = useI18n();
  const q = useApi("warehouse", "/staff/orders/warehouse", undefined, { staleTime: 0, refetchInterval: 30000 });
  return (
    <div className="space-y-5" data-testid="warehouse-page">
      <div><h1 className="text-3xl font-bold text-navy">{t("ops.warehouseTitle")}</h1><p className="mt-1 text-sm text-slate-500">{t("ops.warehouseSub")}</p></div>
      {q.isLoading ? <SkeletonBlock className="h-64" /> : !q.data?.length ? <EmptyState icon={PackageOpen} title={t("ops.noWarehouse")} testId="warehouse-empty" /> : q.data.map((g) => (
        <section key={`${g.date}-${g.area_code}`} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`wh-group-${g.date}-${g.area_code}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold capitalize text-navy md:text-lg">{g.date ? `${fmtWeekday(g.date, lang)} ${fmtDate(g.date)}` : "—"} · {g.area_name}</h2>
            <a href={`/staff/warehouse/print?date=${g.date}&area=${g.area_code}`} target="_blank" rel="noreferrer" data-testid={`wh-print-${g.date}-${g.area_code}`} className="flex h-11 items-center gap-2 rounded-full border border-slate-200 px-4 text-sm font-semibold text-navy hover:bg-slate-50"><Printer className="h-4 w-4" /> {t("ops.print")}</a>
          </div>
          <ul className="divide-y divide-slate-100">{g.orders.map((o) => <OrderCard key={o.local_id} o={o} />)}</ul>
        </section>
      ))}
    </div>
  );
}
